import React, { useState, useEffect, useCallback } from 'react';
import {
  FaBuilding, FaPlus, FaEdit, FaTrash, FaUndo,
  FaTimes, FaSave, FaLock, FaExchangeAlt, FaGasPump
} from 'react-icons/fa';
import { useApiCall } from '../hooks/useApiCall';
import { useToast } from '../components/Toast';
import { useModal } from '../components/Modal';
import SortTh from '../components/SortTh';
import '../styles/Reference.css';

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Vendor Detail Modal ────────────────────────────────────────────────────
function VendorDetail({ vendorId }) {
  const apiCall = useApiCall();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const res = await apiCall('admin/vendors/detail', 'POST', { id: vendorId, page: p });
    if (res?.success) setData(res.data);
    setLoading(false);
  }, [vendorId]);

  useEffect(() => { load(1); }, []);

  if (loading && !data) return <div className="table-empty" style={{ padding: 32 }}>Loading…</div>;
  if (!data) return <div className="table-empty" style={{ padding: 32 }}>Failed to load.</div>;

  const { vendor, stats, transactions, total } = data;
  const pages = Math.ceil(total / 25);

  return (
    <div className="entity-detail">
      <div className="entity-detail-stats">
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.total_txns}</div>
          <div className="entity-stat-label">Transactions</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.total_volume.toLocaleString()} <span className="entity-stat-unit">{vendor.gas_type}</span></div>
          <div className="entity-stat-label">Total resupplied</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.first_ts ? new Date(stats.first_ts * 1000).toLocaleDateString() : '—'}</div>
          <div className="entity-stat-label">First activity</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.last_ts ? new Date(stats.last_ts * 1000).toLocaleDateString() : '—'}</div>
          <div className="entity-stat-label">Last activity</div>
        </div>
      </div>

      <div className="entity-detail-txns">
        <div className="entity-detail-txns-header">Transactions</div>
        {transactions.length === 0 ? (
          <div className="table-empty" style={{ padding: 20 }}>No transactions found.</div>
        ) : (
          <>
            <table className="data-table">
              <thead><tr><th>#</th><th>Type</th><th>Tanker</th><th>Amount</th><th>Fueler</th><th>Date</th></tr></thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id}>
                    <td className="txn-id-cell">#{tx.id}</td>
                    <td><span className="txn-badge txn-badge-in">IN</span></td>
                    <td>{tx.tanker || '—'}</td>
                    <td>+{tx.amount} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{tx.amount_type}</span></td>
                    <td>{tx.fueler || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmtTs(tx.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pages > 1 && (
              <div className="bc-pagination-row">
                <button className="btn btn-sm btn-secondary" onClick={() => { setPage(p => p - 1); load(page - 1); }} disabled={page === 1}>←</button>
                <span>{page} / {pages}</span>
                <button className="btn btn-sm btn-secondary" onClick={() => { setPage(p => p + 1); load(page + 1); }} disabled={page === pages}>→</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Vendor Form ──────────────────────────────────────────────────────────────
function VendorForm({ vendor, gasTypes, onSave, closeModal }) {
  const [form, setForm] = useState(
    vendor
      ? { id: vendor.id, name: vendor.name, gas_type_id: vendor.gas_type_id }
      : { id: 0, name: '', gas_type_id: gasTypes[0]?.id || 1 }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form id="vendor-form" onSubmit={e => { e.preventDefault(); onSave(form); closeModal(); }}>
      <div className="form-row">
        <label className="form-label">Vendor name <span className="required">*</span></label>
        <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. SurFuel Ltd." />
      </div>
      <div className="form-row">
        <label className="form-label">Fuel type</label>
        <select className="form-input" value={form.gas_type_id} onChange={e => set('gas_type_id', Number(e.target.value))}>
          {gasTypes.map(g => <option key={g.id} value={g.id}>{g.type}</option>)}
        </select>
      </div>
    </form>
  );
}

// ─── RefTable ──────────────────────────────────────────────────────────────────────────────
function RefTable({ items, columns, sortKeys, renderRow, onEdit, onDelete, onRestore, onQR, onRowClick }) {
  const [sortBy,  setSortBy]  = useState(0);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (idx) => {
    setSortBy(idx);
    setSortDir(sortBy === idx && sortDir === 'asc' ? 'desc' : 'asc');
  };

  const sortItems = (list) => {
    if (!sortKeys?.[sortBy]) return list;
    return [...list].sort((a, b) => {
      const key = sortKeys[sortBy];
      const av = a[key] ?? '', bv = b[key] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  const active   = sortItems(items.filter(i => !i.archived));
  const archived = sortItems(items.filter(i =>  i.archived));

  const renderSection = (list, isArchived) => list.map(item => {
    const cells = renderRow(item);
    return (
      <tr key={item.id} className={`${isArchived ? 'ref-row-archived' : ''} ${!isArchived && onRowClick ? 'ref-row-clickable' : ''}`}
          onClick={!isArchived && onRowClick ? () => onRowClick(item) : undefined}>
        {cells.map((c, i) => <td key={i}>{c}</td>)}
        <td>
          {isArchived ? (
            <div className="table-actions">
              <span className="ref-badge-archived">Archived</span>
              <button className="btn btn-sm btn-secondary" title="Restore" onClick={() => onRestore(item)}><FaUndo /></button>
            </div>
          ) : (
            <div className="table-actions">
              {item.txn_count > 0 && <span className="ref-badge-linked" title={`${item.txn_count} linked transaction(s)`}>{item.txn_count} txns</span>}
              {onQR && <button className="btn btn-sm btn-secondary" title="QR & Image" onClick={() => onQR(item)}><FaUndo /></button>}
              <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onEdit(item); }}><FaEdit /></button>
              {item.txn_count > 0
                ? <button className="btn btn-sm btn-secondary" disabled title="Cannot delete — linked to transactions" style={{ opacity: 0.4, cursor: 'not-allowed' }}><FaLock /></button>
                : <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); onDelete(item); }}><FaTrash /></button>
              }
            </div>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className="users-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c, i) => sortKeys?.[i]
              ? <SortTh key={c} col={i} label={c} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              : <th key={c}>{c}</th>
            )}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {active.length === 0 && archived.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="table-empty">No vendors yet. Add one above.</td></tr>
          ) : (
            <>
              {renderSection(active, false)}
              {archived.length > 0 && (
                <>
                  <tr className="ref-archived-header">
                    <td colSpan={columns.length + 1}>
                      <span className="ref-archived-label">Archived (historical records kept)</span>
                    </td>
                  </tr>
                  {renderSection(archived, true)}
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Vendors() {
  const apiCall = useApiCall();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();

  const [vendors,  setVendors]  = useState([]);
  const [gasTypes, setGasTypes] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    const res = await apiCall('admin/vendors', 'POST', {});
    if (res?.success) {
      setVendors(res.data.vendors || []);
      if (res.data.gas_types?.length) setGasTypes(res.data.gas_types);
    }
  }, [apiCall]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  const openDetail = (vendor) => {
    openModal(
      <><FaBuilding style={{ marginRight: 8, opacity: 0.7 }} />{vendor.name}</>,
      <VendorDetail vendorId={vendor.id} />,
      'lg'
    );
  };

  const openForm = (vendor = null) => {
    openModal(
      vendor ? 'Edit Vendor' : 'Add Vendor',
      <VendorForm
        vendor={vendor}
        gasTypes={gasTypes}
        onSave={async (form) => {
          const res = await apiCall('admin/vendors/save', 'POST', { form });
          if (res?.success) { showToast(vendor ? 'Vendor updated.' : 'Vendor added.', 'success'); load(); }
          else showToast(res?.response || 'Error saving vendor.', 'error');
        }}
        closeModal={closeModal}
      />,
      'sm',
      {
        closeOnBackdrop: false,
        footer: (closeModal) => (
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
            <button type="submit" form="vendor-form" className="btn btn-primary"><FaSave /> {vendor ? 'Save changes' : 'Add vendor'}</button>
          </>
        )
      }
    );
  };

  const confirmDelete = (vendor) => {
    openModal(
      'Delete Vendor',
      <div className="delete-confirmation">
        <p>Permanently delete <strong>"{vendor.name}"</strong>?</p>
        <p className="warning-text">This cannot be undone.</p>
      </div>,
      'sm',
      {
        footer: (closeModal) => (
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={async () => {
              const res = await apiCall('admin/vendors/delete', 'POST', { id: vendor.id });
              if (res?.success) { showToast('Vendor deleted.', 'success'); load(); }
              else showToast(res?.response || 'Error.', 'error');
              closeModal();
            }}>Delete</button>
          </>
        )
      }
    );
  };

  const restore = async (vendor) => {
    const res = await apiCall('admin/vendors/restore', 'POST', { id: vendor.id });
    if (res?.success) { showToast('Vendor restored.', 'success'); load(); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><FaBuilding style={{ marginRight: 10, opacity: 0.7 }} />Vendors</h1>
          <p className="page-subtitle">Fuel suppliers linked to resupply transactions</p>
        </div>
        <button className="btn btn-primary" onClick={() => openForm()}>
          <FaPlus style={{ marginRight: 6 }} /> Add Vendor
        </button>
      </div>

      {loading ? (
        <div className="table-empty" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <RefTable
          items={vendors}
          columns={['Name', 'Fuel type', 'Transactions']}
          sortKeys={['name', 'gas_type', 'txn_count']}
          renderRow={v => [v.name, v.gas_type || '—', v.txn_count]}
          onEdit={openForm}
          onDelete={confirmDelete}
          onRestore={restore}
          onRowClick={openDetail}
        />
      )}
    </div>
  );
}
